'use client';

import { useCallback } from 'react';

import { ScreenReaderOnly, Typography } from '@capsule/consumer-design-system';

import { useLocale, useTranslations } from 'next-intl';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';

const LanguageSelectorContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
  width: 100%;
`;

const LanguageSelectorWrapper = styled.div`
  width: 100%;
  padding-top: ${({ theme }) => theme.space.s4};
  margin-bottom: ${({ theme }) => theme.space.s2};
`;

const LanguageDropdown = styled.select`
  padding: 0.5rem;
  border: 1px solid ${({ theme }) => theme.colors.capsuleGreen60};
  border-radius: ${({ theme }) => theme.radii.r100};
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.capsuleGreen60};
  cursor: pointer;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.capsuleGreen80};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.capsuleGreen60};
  }
`;

const LanguageLabel = styled(Typography)`
  margin-right: 0.5rem;
  display: flex;
  align-items: center;
`;

const HiddenDescription = styled(ScreenReaderOnly)`
  display: block;
  height: 0;
`;

export const LanguageSelector = () => {
  const t = useTranslations('languageSelector');
  const locale = useLocale();
  const history = useHistory();
  const location = useLocation();

  const handleLanguageChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newLanguage = e.target.value;
      const isNewLanguageSpanish = newLanguage === 'es';

      const currentPathParts = location.pathname.split('/');

      // Remove language prefix if it exists
      const pathWithoutLang = currentPathParts.filter(part => part !== 'es' && part !== '').join('/');

      // Construct new path with or without language prefix
      const newPath = isNewLanguageSpanish ? `/es/${pathWithoutLang}` : `/${pathWithoutLang}`;

      history.push({
        pathname: newPath,
        search: location.search,
      });
    },
    [history, location]
  );

  // IDs for ARIA attributes
  const languageSelectorId = 'language-selector';
  const descriptionId = 'language-selector-description';

  return (
    <LanguageSelectorWrapper>
      <LanguageSelectorContainer>
        <LanguageLabel variant="body" as="label" htmlFor={languageSelectorId}>
          {t('label')}
        </LanguageLabel>
        <HiddenDescription id={descriptionId}>{t('description')}</HiddenDescription>
        <LanguageDropdown
          id={languageSelectorId}
          value={locale}
          onChange={handleLanguageChange}
          aria-describedby={descriptionId}
          aria-label={t('ariaLabel')}
          aria-live="polite"
        >
          <option value="en">{t('languages.english')}</option>
          <option value="es">{t('languages.spanish')}</option>
        </LanguageDropdown>
      </LanguageSelectorContainer>
    </LanguageSelectorWrapper>
  );
};
